import { Dialog, Slide, Transitions, Link, Box, Typography, Button } from '@material-ui/core';
import cs from 'classnames';
import { Form, Formik } from 'formik';
import React, { forwardRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useCurrentUser } from '../../../Session';
import { PurchaseTransactionStage, UserType } from '../../../api';
import MobileAppBar from '../../../components/MobileAppBar';
import OutlinedDatePickerField from '../../../components/fields/OutlinedDatePickerField';
import OutlinedSelectField from '../../../components/fields/OutlinedSelectField';
import useCommonStyles from '../../../useCommonStyles';
import usePurchaseTransactionStateFilterOptoins from '../../../utilities/usePurchaseTransactionStateFilterOptoins';
import useFilterDialogStyles from './useFilterDialogStyles';

export type FilterDialogProps = {
    open: boolean;
    setOpen: (value: boolean) => void;
    setValues: (stage, createdAt, targetHandoverDate) => void;
    stage: 'all' | PurchaseTransactionStage;
    createdAt: Date | null;
    targetHandoverDate: Date | null;
};

const Transition = forwardRef((props: Transitions & { children?: React.ReactElement }, ref: React.Ref<unknown>) => (
    <Slide ref={ref} direction="down" {...props} />
));

const FilterDialog = ({ open, setOpen, setValues, stage, createdAt, targetHandoverDate }: FilterDialogProps) => {
    const { t } = useTranslation(['common', 'purchaseTransactionsPage']);
    const commonStyles = useCommonStyles();
    const styles = useFilterDialogStyles();
    const currentUser = useCurrentUser();

    const statuses = usePurchaseTransactionStateFilterOptoins();

    const onSubmit = useCallback(
        (values: { stage: string; createdAt: Date | string; targetHandoverDate: Date | string }) => {
            const { stage, createdAt, targetHandoverDate } = values;

            setValues(stage, createdAt, targetHandoverDate);
            setOpen(false);
        },
        [setOpen, setValues]
    );

    return (
        <Dialog TransitionComponent={Transition} open={open} fullScreen>
            <Formik initialValues={{ stage, createdAt, targetHandoverDate }} onSubmit={onSubmit}>
                {({ setFieldValue }) => (
                    <>
                        <MobileAppBar>
                            <Link
                                className={cs(commonStyles.displayFlex, commonStyles.primary2)}
                                component="a"
                                onClick={() => setOpen(false)}
                                underline="none"
                            >
                                {t('common:cancel')}
                            </Link>
                            <Box display="flex" justifyContent="center" width="100%">
                                <Typography className={cs(commonStyles.bigFontSize)}>{t('common:filter')}</Typography>
                            </Box>
                            <Link
                                className={cs(commonStyles.displayFlex, commonStyles.primary2)}
                                component="a"
                                onClick={() => {
                                    setFieldValue('stage', 'all');
                                    setFieldValue('createdAt', null);
                                    setFieldValue('targetHandoverDate', null);
                                }}
                                underline="none"
                            >
                                {t('common:reset')}
                            </Link>
                        </MobileAppBar>
                        <Form>
                            <Box className={styles.content} display="flex" flexDirection="column">
                                <Box mt={3} width="100%">
                                    <OutlinedSelectField
                                        color="secondary"
                                        inputProps={{
                                            classes: {
                                                icon: commonStyles.fillSecondary,
                                            },
                                        }}
                                        label={t('purchaseTransactionsPage:state')}
                                        name="stage"
                                        options={statuses}
                                    />
                                </Box>
                                <Box mt={3} width="100%">
                                    <OutlinedDatePickerField
                                        color="secondary"
                                        label={t('purchaseTransactionsPage:filter.createdAt')}
                                        name="createdAt"
                                        placeholder={t('purchaseTransactionsPage:filter.chooseDate')}
                                    />
                                </Box>
                                {currentUser.type === UserType.Admin && (
                                    <Box mt={3} width="100%">
                                        <OutlinedDatePickerField
                                            color="secondary"
                                            label={t('purchaseTransactionsPage:filter.targetHandoverDate')}
                                            name="targetHandoverDate"
                                            placeholder={t('purchaseTransactionsPage:filter.chooseDate')}
                                        />
                                    </Box>
                                )}
                            </Box>
                            <Box className={styles.appltFilters}>
                                <Box alignSelf="center" margin="auto" width="100%">
                                    <Button
                                        className={styles.applyButton}
                                        color="secondary"
                                        size="large"
                                        type="submit"
                                        variant="contained"
                                    >
                                        {t('purchaseTransactionsPage:filter.applyFilters')}
                                    </Button>
                                </Box>
                            </Box>
                        </Form>
                    </>
                )}
            </Formik>
        </Dialog>
    );
};

export default FilterDialog;
